import React, { useState } from "react";
import { makeStyles } from "@material-ui/core/styles";
import {
  Paper, IconButton, Tooltip, Typography, Menu, MenuItem,
} from "@material-ui/core";
import CloseIcon from "@material-ui/icons/Close";
import CheckCircleOutlineIcon from "@material-ui/icons/CheckCircleOutline";
import DoneAllIcon from "@material-ui/icons/DoneAll";
import SwapHorizIcon from "@material-ui/icons/SwapHoriz";
import DeleteOutlineIcon from "@material-ui/icons/DeleteOutline";
import DeleteForeverIcon from "@material-ui/icons/DeleteForever";
import RestoreIcon from "@material-ui/icons/Restore";
import MoreVertIcon from "@material-ui/icons/MoreVert";
import { i18n } from "../../translate/i18n";

const useStyles = makeStyles((theme) => ({
  bar: {
    display: "flex",
    alignItems: "center",
    gap: 4,
    padding: "4px 8px",
    backgroundColor: theme.palette.type === "dark" ? "#1b5e20" : "#25D366",
    color: "#fff",
    minHeight: 44,
  },
  countText: {
    flex: 1,
    fontSize: 14,
    fontWeight: 600,
    color: "#fff",
    marginLeft: 4,
  },
  iconBtn: {
    color: "#fff",
    padding: 6,
  },
  iconBtnDanger: {
    color: "#ffcdd2",
    padding: 6,
  },
}));

/**
 * AppBar contextual del modo selección múltiple.
 *
 * Props:
 *  - count: número de tickets seleccionados
 *  - isAdmin: bool — habilita eliminar definitivo (hard-delete)
 *  - deletedView: bool — vista de Eliminados (muestra Restaurar para todos, Eliminar definitivo solo admin)
 *  - onExit, onMarkRead, onResolve, onTransfer, onDelete, onRestore, onHardDelete, onSelectAllVisible
 */
const BatchActionBar = ({
  count = 0,
  isAdmin = false,
  deletedView = false,
  onExit,
  onMarkRead,
  onResolve,
  onTransfer,
  onDelete,
  onRestore,
  onHardDelete,
  onSelectAllVisible,
}) => {
  const classes = useStyles();
  const [menuAnchor, setMenuAnchor] = useState(null);

  return (
    <Paper square elevation={0} className={classes.bar}>
      <Tooltip title={i18n.t("batchSelection.exit")} arrow>
        <IconButton size="small" className={classes.iconBtn} onClick={onExit}>
          <CloseIcon fontSize="small" />
        </IconButton>
      </Tooltip>
      <Typography className={classes.countText}>
        {i18n.t("batchSelection.count", { count })}
      </Typography>

      {!deletedView && (
        <>
          <Tooltip title={i18n.t("batchSelection.markRead")} arrow>
            <span>
              <IconButton
                size="small"
                className={classes.iconBtn}
                onClick={onMarkRead}
                disabled={count === 0}
              >
                <DoneAllIcon fontSize="small" />
              </IconButton>
            </span>
          </Tooltip>
          <Tooltip title={i18n.t("batchSelection.resolve")} arrow>
            <span>
              <IconButton
                size="small"
                className={classes.iconBtn}
                onClick={onResolve}
                disabled={count === 0}
              >
                <CheckCircleOutlineIcon fontSize="small" />
              </IconButton>
            </span>
          </Tooltip>
          <Tooltip title={i18n.t("batchSelection.transfer")} arrow>
            <span>
              <IconButton
                size="small"
                className={classes.iconBtn}
                onClick={onTransfer}
                disabled={count === 0}
              >
                <SwapHorizIcon fontSize="small" />
              </IconButton>
            </span>
          </Tooltip>
          <Tooltip title={i18n.t("batchSelection.delete")} arrow>
            <span>
              <IconButton
                size="small"
                className={classes.iconBtn}
                onClick={onDelete}
                disabled={count === 0}
              >
                <DeleteOutlineIcon fontSize="small" />
              </IconButton>
            </span>
          </Tooltip>
        </>
      )}

      {deletedView && (
        <>
          <Tooltip title={i18n.t("batchSelection.restore")} arrow>
            <span>
              <IconButton
                size="small"
                className={classes.iconBtn}
                onClick={onRestore}
                disabled={count === 0}
              >
                <RestoreIcon fontSize="small" />
              </IconButton>
            </span>
          </Tooltip>
          {isAdmin && (
            <Tooltip title={i18n.t("batchSelection.hardDelete")} arrow>
              <span>
                <IconButton
                  size="small"
                  className={classes.iconBtnDanger}
                  onClick={onHardDelete}
                  disabled={count === 0}
                >
                  <DeleteForeverIcon fontSize="small" />
                </IconButton>
              </span>
            </Tooltip>
          )}
        </>
      )}

      <Tooltip title={i18n.t("batchSelection.more")} arrow>
        <IconButton
          size="small"
          className={classes.iconBtn}
          onClick={(e) => setMenuAnchor(e.currentTarget)}
        >
          <MoreVertIcon fontSize="small" />
        </IconButton>
      </Tooltip>
      <Menu
        anchorEl={menuAnchor}
        open={Boolean(menuAnchor)}
        onClose={() => setMenuAnchor(null)}
      >
        <MenuItem
          onClick={() => {
            setMenuAnchor(null);
            if (onSelectAllVisible) onSelectAllVisible();
          }}
        >
          {i18n.t("batchSelection.selectAllVisible")}
        </MenuItem>
      </Menu>
    </Paper>
  );
};

export default BatchActionBar;
